import {
  ActivatedRouteSnapshot,
  DetachedRouteHandle,
  RouteReuseStrategy,
} from '@angular/router';

export class PortusRouteReuseStrategy implements RouteReuseStrategy {
  private static storedRoutes =
    new Map<string, DetachedRouteHandle>();

  private static routesToDiscard =
    new Set<string>();

  shouldDetach(route: ActivatedRouteSnapshot): boolean {
    const key = this.getRouteKey(route);

    /*
     * Tab manually close hua ho to route ko cache mat karo.
     */
    if (
      PortusRouteReuseStrategy.routesToDiscard.has(key)
    ) {
      PortusRouteReuseStrategy.routesToDiscard.delete(key);
      return false;
    }

    return this.isReusableRoute(route);
  }

  store(
    route: ActivatedRouteSnapshot,
    handle: DetachedRouteHandle | null
  ): void {
    if (!handle) {
      return;
    }

    const key = this.getRouteKey(route);

    /*
     * Close ki hui tab ka detached handle store nahi hoga.
     */
    if (
      PortusRouteReuseStrategy.routesToDiscard.has(key)
    ) {
      PortusRouteReuseStrategy.routesToDiscard.delete(key);
      return;
    }

    PortusRouteReuseStrategy.storedRoutes.set(
      key,
      handle
    );
  }

  shouldAttach(route: ActivatedRouteSnapshot): boolean {
    if (!this.isReusableRoute(route)) {
      return false;
    }

    const key = this.getRouteKey(route);

    /*
     * Marked-for-discard route kabhi restore nahi hoga.
     */
    if (
      PortusRouteReuseStrategy.routesToDiscard.has(key)
    ) {
      return false;
    }

    return PortusRouteReuseStrategy.storedRoutes.has(
      key
    );
  }

  retrieve(
    route: ActivatedRouteSnapshot
  ): DetachedRouteHandle | null {
    if (!this.isReusableRoute(route)) {
      return null;
    }

    const key = this.getRouteKey(route);

    if (
      PortusRouteReuseStrategy.routesToDiscard.has(key)
    ) {
      return null;
    }

    return (
      PortusRouteReuseStrategy.storedRoutes.get(key) ??
      null
    );
  }

  shouldReuseRoute(
    future: ActivatedRouteSnapshot,
    current: ActivatedRouteSnapshot
  ): boolean {
    /*
     * Different Angular route configs should not reuse
     * the current component.
     */
    if (future.routeConfig !== current.routeConfig) {
      return false;
    }

    /*
     * Reusable pages with different query parameters
     * should be treated as separate workspace tabs.
     */
    if (
      this.isReusableRoute(future) ||
      this.isReusableRoute(current)
    ) {
      return (
        this.getRouteKey(future) ===
        this.getRouteKey(current)
      );
    }

    return true;
  }

  /**
   * Clear one workspace tab's cached route.
   */
  static clearRouteByKey(key: string): void {
    if (!key) {
      return;
    }

    /*
     * Delete an already detached cached route.
     */
    PortusRouteReuseStrategy.storedRoutes.delete(key);

    /*
     * Prevent the currently active route from being stored
     * when Angular navigates away after the tab is closed.
     */
    PortusRouteReuseStrategy.routesToDiscard.add(key);
  }

  /**
   * Clear every cached workspace route.
   *
   * Use this method during logout.
   */
  static clearAll(): void {
    PortusRouteReuseStrategy.storedRoutes.clear();
    PortusRouteReuseStrategy.routesToDiscard.clear();
  }

  /**
   * Optional alias.
   *
   * Keep this only if some existing code calls
   * PortusRouteReuseStrategy.clearAllRoutes().
   */
  static clearAllRoutes(): void {
    PortusRouteReuseStrategy.clearAll();
  }

  private isPagesRoute(
    route: ActivatedRouteSnapshot
  ): boolean {
    const fullPath = this.getFullPath(route);

    return fullPath.startsWith('pages/');
  }

  private isReusableRoute(
    route: ActivatedRouteSnapshot
  ): boolean {
    /*
     * Cache only actual routed components.
     */
    if (!route.routeConfig || !route.component) {
      return false;
    }

    /*
     * Parent routes with child outlets should not be cached.
     */
    if (route.firstChild) {
      return false;
    }

    /*
     * Explicit reuse=false always takes priority.
     */
    if (route.data?.['reuse'] === false) {
      return false;
    }

    /*
     * Workspace tabs should switch instantly. Cache page components again,
     * while still respecting explicit reuse=false on sensitive routes.
     */
    return (
      route.data?.['reuse'] === true ||
      this.isPagesRoute(route)
    );
  }

  private getRouteKey(
    route: ActivatedRouteSnapshot
  ): string {
    const fullPath = this.getFullPath(route);

    const queryParams = {
      ...(route.queryParams || {}),
    };

    /*
     * reset is a temporary parameter and should not
     * create a separate workspace tab/cache.
     */
    delete queryParams['reset'];

    const sortedQueryParams = Object.keys(queryParams)
      .sort()
      .reduce(
        (
          result: Record<string, unknown>,
          queryKey: string
        ) => {
          result[queryKey] =
            queryParams[queryKey];

          return result;
        },
        {}
      );

    return `${fullPath}::${JSON.stringify(
      sortedQueryParams
    )}`;
  }

  private getFullPath(
    route: ActivatedRouteSnapshot
  ): string {
    return route.pathFromRoot
      .flatMap((snapshot) =>
        snapshot.url.map(
          (segment) => segment.path
        )
      )
      .filter(Boolean)
      .join('/');
  }
}
